import React, { useState, useEffect, useCallback } from 'react';
import {
  UnauthorizedError,
  getActuals,
  uploadActualsDocument,
} from '../services/api';

/**
 * ActualsUploadPanel — F5 Upload-Actuals for already-operating businesses.
 *
 * Shown in the right-panel slot (same pattern as BusinessPlanPanel) while
 * interview_stage is `actuals_upload` or `actuals_confirm`.
 *
 *  actuals_upload view  — upload slots for P&L (required), Balance Sheet
 *                         (required), Cash Flow (optional), with per-file
 *                         extraction status.
 *  actuals_confirm view — a table of the extracted figures with editable cells,
 *                         an existing-debts section, and a "Confirm and Continue"
 *                         button that asks the user to confirm via chat (the
 *                         confirm_actuals tool runs through the conversation, so
 *                         corrections land in the same audited path).
 *
 * The panel never computes anything — it displays extracted figures and lets
 * the user correct them. Calibration + projection happen server-side.
 */

const DOC_SLOTS = [
  { type: 'pl_statement', label: 'Profit & Loss / Income Statement', required: true },
  { type: 'balance_sheet', label: 'Balance Sheet', required: true },
  { type: 'cash_flow', label: 'Cash Flow Statement', required: false },
];

const PL_FIELDS = [
  ['annual_revenue', 'Annual Revenue'],
  ['cogs', 'Cost of Goods Sold'],
  ['gross_profit', 'Gross Profit'],
  ['labor_cost', 'Labor Cost'],
  ['rent_expense', 'Rent'],
  ['marketing_expense', 'Marketing'],
  ['utilities_expense', 'Utilities'],
  ['other_operating_expenses', 'Other Operating Expenses'],
  ['net_income', 'Net Income'],
  ['ebitda', 'EBITDA'],
];
const BS_FIELDS = [
  ['total_assets', 'Total Assets'],
  ['total_liabilities', 'Total Liabilities'],
  ['owners_equity', "Owner's Equity"],
  ['cash_and_equivalents', 'Cash & Equivalents'],
  ['accounts_receivable', 'Accounts Receivable'],
  ['inventory', 'Inventory'],
  ['accounts_payable', 'Accounts Payable'],
];

function fmt(v) {
  return v === null || v === undefined || v === '' ? '—' : String(v);
}

export default function ActualsUploadPanel({
  sessionId,
  stage,
  refreshSignal,
  onSendMessage,
  title = 'Upload Financials',
  open = true,
  onClose = () => {},
}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [uploadingType, setUploadingType] = useState(null);
  const [slotError, setSlotError] = useState({});
  const [edits, setEdits] = useState({}); // {docType: {field: value}}

  const refresh = useCallback(async () => {
    try {
      setData(await getActuals(sessionId));
    } catch (err) {
      if (!(err instanceof UnauthorizedError)) setError(err.message);
    }
  }, [sessionId]);

  useEffect(() => { refresh(); }, [refresh, refreshSignal]);

  const handleFile = async (docType, file) => {
    if (!file) return;
    setUploadingType(docType);
    setSlotError((p) => ({ ...p, [docType]: null }));
    try {
      await uploadActualsDocument(sessionId, docType, file);
      await refresh();
    } catch (err) {
      if (!(err instanceof UnauthorizedError)) {
        setSlotError((p) => ({ ...p, [docType]: err.message }));
      }
    } finally {
      setUploadingType(null);
    }
  };

  const setEdit = (docType, field, value) => {
    setEdits((p) => ({ ...p, [docType]: { ...(p[docType] || {}), [field]: value } }));
  };

  const handleConfirm = () => {
    // Build a plain-English confirmation; any edited cells become corrections.
    const corrections = Object.entries(edits)
      .filter(([, fields]) => Object.keys(fields).length > 0)
      .map(([doc, fields]) => {
        const parts = Object.entries(fields).map(([f, v]) => `${f} = ${v}`);
        return `${doc}: ${parts.join(', ')}`;
      });
    const msg = corrections.length
      ? `These figures look right, with these corrections — ${corrections.join('; ')}.`
      : 'These figures look right — please confirm and continue.';
    onSendMessage(msg);
  };

  const shell = (body) => (
    <aside
      className={`data-panel ${open ? 'data-panel-open' : 'data-panel-closed'}`}
      aria-label={title}
    >
      <header className="data-panel-header">
        <h2>{title}</h2>
        <button type="button" className="data-panel-close-btn" aria-label="Close panel" onClick={onClose}>×</button>
      </header>
      <div className="data-panel-body">{body}</div>
    </aside>
  );

  if (!data) {
    return shell(
      <>
        <p className="sba-panel-status">Loading…</p>
        {error && <div className="sba-panel-error">{error}</div>}
      </>
    );
  }

  const extracted = data.actuals_extracted || {};
  const haveTypes = new Set((data.actuals_uploaded || []).map((u) => u.document_type));

  // ── Upload view ──────────────────────────────────────────────────────────
  if (stage === 'actuals_upload') {
    return shell(
      <>
        <p className="sba-panel-status">
          Your business is already operating, so we'll start from your real
          financials. Upload your statements below (PDF, Excel, or CSV — up to
          10&nbsp;MB each). We'll extract the figures for you to review.
        </p>
        <ul className="sba-checklist">
          {DOC_SLOTS.map((slot) => {
            const busy = uploadingType === slot.type;
            const done = haveTypes.has(slot.type);
            return (
              <li key={slot.type} className="sba-checklist-item">
                <div className="sba-item-row">
                  <span className={`sba-chip sba-chip-${done ? 'complete' : 'missing'}`}>
                    {done ? '✓' : '○'}
                  </span>
                  <span className="sba-item-label">
                    {slot.label}
                    {!slot.required && <span className="sba-optional"> (optional)</span>}
                  </span>
                  <label className={`sba-upload-btn ${busy ? 'disabled' : ''}`}>
                    {busy ? 'Extracting…' : done ? 'Replace' : 'Upload'}
                    <input
                      type="file"
                      accept=".pdf,.xlsx,.csv"
                      hidden
                      disabled={busy}
                      onChange={(e) => {
                        const f = e.target.files && e.target.files[0];
                        e.target.value = '';
                        handleFile(slot.type, f);
                      }}
                    />
                  </label>
                </div>
                {slotError[slot.type] && <div className="sba-slot-error">{slotError[slot.type]}</div>}
              </li>
            );
          })}
        </ul>
        {data.required_remaining && data.required_remaining.length > 0 ? (
          <p className="sba-panel-status">
            Still needed: {data.required_remaining.join(', ')}.
          </p>
        ) : data.all_uploaded ? (
          <p className="sba-panel-status">All statements received — review is next.</p>
        ) : (
          <p className="sba-panel-status">
            Both required statements received — review is next. (You can still
            add the optional cash flow statement.)
          </p>
        )}
        {error && <div className="sba-panel-error">{error}</div>}
      </>
    );
  }

  // ── Confirm view ─────────────────────────────────────────────────────────
  const renderTable = (docType, fields) => {
    const figures = extracted[docType] || {};
    return (
      <table className="actuals-table">
        <tbody>
          {fields.map(([key, label]) => (
            <tr key={key}>
              <td className="actuals-field">{label}</td>
              <td className="actuals-value">
                <input
                  className="actuals-input"
                  defaultValue={fmt(figures[key])}
                  onChange={(e) => setEdit(docType, key, e.target.value)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const debts = (extracted.balance_sheet && extracted.balance_sheet.existing_debts) || [];

  return shell(
    <>
      <p className="sba-panel-status">
        Here's what we extracted from your statements. Edit any value that's
        wrong, then confirm to continue.
      </p>

      <h3 className="actuals-section-title">Profit &amp; Loss</h3>
      {renderTable('pl_statement', PL_FIELDS)}

      <h3 className="actuals-section-title">Balance Sheet</h3>
      {renderTable('balance_sheet', BS_FIELDS)}

      <h3 className="actuals-section-title">Existing Debts</h3>
      {debts.length === 0 ? (
        <p className="sba-panel-status">No existing debts were found.</p>
      ) : (
        <table className="actuals-table">
          <thead>
            <tr><th>Creditor</th><th>Balance</th><th>Monthly</th><th>Maturity</th></tr>
          </thead>
          <tbody>
            {debts.map((d, i) => (
              <tr key={i}>
                <td>{fmt(d.creditor)}</td>
                <td>{fmt(d.balance)}</td>
                <td>{fmt(d.monthly_payment)}</td>
                <td>{fmt(d.maturity_date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="sba-download">
        <button className="sba-btn sba-btn-primary" onClick={handleConfirm}>
          Confirm and Continue
        </button>
      </div>
      {error && <div className="sba-panel-error">{error}</div>}
    </>
  );
}
